import {
  INBOUND_CORRECTION_LABELS,
  INBOUND_STATUSES,
  PACKAGING_LEVEL_LABELS,
  normalizeScan,
  parseLabelSize,
  productLabelZpl,
  type CloseInboundResult,
  type DockBarcode,
  type DockBarcodesTaught,
  type ExpectingShipment,
  type InboundCorrectionResult,
  type InboundCorrectionView,
  type InboundReceiptView,
  type InboundShipment,
  type InboundShipmentLine,
  type InboundShipmentSummary,
  type ProductSize,
  type ReceiveScanResult,
  type UndoneReceipt,
  type UnknownBarcodeDetails,
} from '@wms/domain';
import { useRef, useState, type KeyboardEvent } from 'react';
import { ApiError, api, errorMessage, type FloorAction } from '../api';
import { feedback } from '../feedback';
import { fmt } from '../format';
import { printZpl } from '../printing';
import { LABEL_SIZE_KEYS, taughtCodes, type LabelCodeChoice } from '../productLabels';
import { acceptedFolds, splitLines } from '../receiving';
import { correctionSummary, countsText, fixPreview, movesText, placesText } from '../receivingFix';
import { navigate } from '../router';
import { createScanRecorder, judgeScan, logRefusal, readerTail, traceAfter, typedCount, type ScanRecorder, type ScanVerdict } from '../scan';
import { store, useSession } from '../session';
import {
  Empty,
  ErrorBanner,
  Modal,
  SCAN_WORDS,
  ScanField,
  ScanWhy,
  SellerChip,
  Spinner,
  TYPED_CODE_REFUSED,
  useFloorAction,
  useLoad,
  useScannerOnly,
  type ActionWords,
  type FloorActionRunner,
} from '../ui';
import { Icon } from '../ui/icons';
import { LabelSizePicker } from '../ui/LabelSize';
import { NextStep } from '../ui/NextStep';
import { useSort } from '../ui/sort';
import { ProductLabelDialog } from './catalog/ProductLabel';

/** Mal kabul at the dock: open the seller's ASN, scan what arrived, close with a discrepancy report. */
export function Receiving({ asn }: { asn: string | null }) {
  return asn ? <ReceiveAsn asnNumber={asn} /> : <InboundList />;
}

const ASN = /^ASN\d+$/i;

/**
 * Where the dock starts: an ASN number opens its shipment; a product barcode scanned here by
 * mistake opens the open shipment that expects the product (or offers the choice).
 */
function AsnScan() {
  const { warehouseId } = useSession();
  const [problem, setProblem] = useState<string | null>(null);
  const [choices, setChoices] = useState<readonly ExpectingShipment[]>([]);
  const open = async (code: string) => {
    setProblem(null);
    setChoices([]);
    if (ASN.test(code)) {
      navigate(`/kabul/${code.toUpperCase()}`);
      return;
    }
    try {
      const found = await api<ExpectingShipment[]>(
        'GET',
        `/api/wms/receiving/expecting?code=${encodeURIComponent(code)}${warehouseId ? `&warehouseId=${warehouseId}` : ''}`,
      );
      const shipments = [...new Set(found.map((f) => f.asnNumber))];
      if (shipments.length === 1) navigate(`/kabul/${shipments[0]}`);
      else if (shipments.length > 1) setChoices(found);
      else {
        feedback('error');
        setProblem(`${code} bir gönderi numarası değil ve bu ürünü bekleyen açık gönderi yok. Kolideki ASN… numarasını okutun.`);
      }
    } catch (e) {
      feedback('error');
      setProblem(errorMessage(e));
    }
  };
  return (
    <section className="card">
      <div className="card-b stack">
        <ScanField
          placeholder="Gönderi numarasını (ASN0000001) ya da bir ürünü okutun"
          allowTyping
          onScan={(code) => void open(code)}
          suggest={{ kinds: ['inbound', 'product'] }}
          hint="Satıcının gönderi numarası (ASN…) koli üstünde veya irsaliyededir. Numara yoksa bir ürünü okutun: o ürünü bekleyen gönderi açılır."
        />
        {problem && <div className="banner bad">{problem}</div>}
        {choices.length > 0 && (
          <div className="stack" style={{ gap: 6 }}>
            <b>Bu ürünü bekleyen gönderiler:</b>
            {choices.map((c) => (
              <button key={c.asnNumber} className="btn" style={{ justifyContent: 'flex-start' }} onClick={() => navigate(`/kabul/${c.asnNumber}`)}>
                <span className="mono">{c.asnNumber}</span> <SellerChip code={c.tenantCode} /> {c.sku} · {fmt.n(c.countedQty)} / {fmt.n(c.expectedQty)} adet
              </button>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

function InboundList() {
  const { warehouseId, isSupervisor, isAdmin } = useSession();
  const list = useLoad(() => api<InboundShipmentSummary[]>('GET', `/api/inbound/shipments${warehouseId ? `?warehouseId=${warehouseId}` : ''}`), [warehouseId], 20_000);
  const sort = useSort<InboundShipmentSummary>('receiving', {
    asn: { label: 'ASN', value: (s) => s.asnNumber },
    seller: { label: 'Satıcı', value: (s) => s.tenantCode },
    reference: { label: 'Referans', value: (s) => s.reference },
    carrier: { label: 'Taşıyıcı', value: (s) => s.carrier },
    expected: { label: 'Beklenen', value: (s) => s.expectedUnits, desc: true },
    status: { label: 'Durum', value: (s) => INBOUND_STATUSES.indexOf(s.status) },
  });
  const open = sort.apply((list.data ?? []).filter((s) => s.status === 'expected' || s.status === 'receiving'));
  // a supervisor reopens one of these when a carton turns up after closing (not once its charge is invoiced);
  // the depo yöneticisi corrects them as they are (an invoiced one: its damaged units)
  const closed = (list.data ?? [])
    .filter((s) => s.status === 'closed' && (isAdmin || !s.receivingInvoiced) && s.closedAt !== null && Date.now() - new Date(s.closedAt).getTime() < 14 * 86_400_000)
    .sort((a, b) => (b.closedAt ?? '').localeCompare(a.closedAt ?? ''))
    .slice(0, 8);
  return (
    <div className="rcv-screen">
      <AsnScan />
      <section className="card">
        <div className="card-h">
          <h2>Beklenen gönderiler</h2>
          <span className="sub">{open.length} açık bildirim</span>
          {sort.picker}
        </div>
        {list.error && <ErrorBanner message={list.error} onRetry={() => void list.reload()} />}
        {!list.data ? (
          <Spinner />
        ) : open.length === 0 ? (
          <Empty title="Beklenen gönderi yok" />
        ) : (
          <div className="table-wrap">
            <table className="t stack-sm">
              <thead>
                <tr>
                  {sort.th('asn')}
                  {sort.th('seller')}
                  {sort.th('reference', 'hide-sm')}
                  {sort.th('carrier', 'hide-sm')}
                  {sort.th('expected', 'num')}
                  {sort.th('status')}
                </tr>
              </thead>
              <tbody>
                {open.map((s) => (
                  <tr key={s.id} className="clickable" onClick={() => navigate(`/kabul/${s.asnNumber}`)}>
                    <td className="mono">{s.asnNumber}</td>
                    <td>
                      <SellerChip code={s.tenantCode} />
                    </td>
                    <td className="hide-sm">{s.reference ?? '—'}</td>
                    <td className="hide-sm">{s.carrier ?? '—'}</td>
                    <td className="num" data-label="Adet">
                      {s.receivedUnits > 0 && `${fmt.n(s.receivedUnits)} / `}
                      {fmt.n(s.expectedUnits)}
                    </td>
                    <td>
                      <span className={`chip ${s.status === 'receiving' ? 'info' : ''}`}>{s.status === 'receiving' ? 'Kabul sürüyor' : 'Bekleniyor'}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
      {isSupervisor && closed.length > 0 && (
        <section className="card">
          <div className="card-h">
            <h2>Son kapatılanlar</h2>
            <span className="sub">
              {isAdmin
                ? 'Sayı yanlışsa açıp kalemdeki “Düzelt”; sonradan koli bulunduysa “Kabulü yeniden aç”'
                : 'Kapattıktan sonra koli bulunduysa açıp “Kabulü yeniden aç”'}
            </span>
          </div>
          <div className="table-wrap">
            <table className="t stack-sm">
              <tbody>
                {closed.map((s) => (
                  <tr key={s.id} className="clickable" onClick={() => navigate(`/kabul/${s.asnNumber}`)}>
                    <td className="mono">{s.asnNumber}</td>
                    <td>
                      <SellerChip code={s.tenantCode} />
                    </td>
                    <td className="num" data-label="Gelen">
                      {fmt.n(s.receivedUnits)} / {fmt.n(s.expectedUnits)}
                    </td>
                    <td className="muted">{fmt.age(s.closedAt!)} önce kapatıldı</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}
    </div>
  );
}

/** Barcodes just saved to a line's product ("Barkod ekle"), for "Barkodu bas". */
interface BarcodesToPrint {
  readonly line: InboundShipmentLine;
  readonly codes: readonly LabelCodeChoice[];
}

/** "open": this device keeps a long shipment's accepted lines unfolded (they start folded) */
const ACCEPTED_KEY = 'wms.receiving.accepted';

/** One scan as sent: kept to send again once the dock has confirmed or taught something. */
interface Take {
  readonly barcode: string;
  /** counted by a supervisor with no barcode read */
  readonly productId?: string;
  readonly qty: number;
  readonly lotNo: string;
  readonly expiry: string;
  readonly damaged: boolean;
  /** barcodes taught for this scan just before: geri al forgets them with it */
  readonly taught?: readonly string[];
}

function ReceiveAsn({ asnNumber }: { asnNumber: string }) {
  const { isSupervisor, isAdmin } = useSession();
  // a manager corrects a line (Düzelt); the corrections card reloads after each
  const [fixing, setFixing] = useState<InboundShipmentLine | null>(null);
  const [corrected, setCorrected] = useState(0);
  const loaded = useLoad(() => api<InboundShipment>('GET', `/api/wms/receiving/asn/${encodeURIComponent(asnNumber)}`), [asnNumber]);
  const [shipment, setShipment] = useState<InboundShipment | null>(null);
  // how many one scan counts (units; cartons for a carton barcode): back to 1 after a scan unless kept
  const [qty, setQty] = useState(1);
  const [keepQty, setKeepQty] = useState(false);
  // barcodes the seller never gave, taught here: after an unknown scan (which then counts) or from a line
  const [teach, setTeach] = useState<{ line?: InboundShipmentLine; scanned?: ScannedUnknown; again?: Take } | null>(null);
  // a refused scan the dock can still confirm (more than expected, or not announced)
  const [confirmable, setConfirmable] = useState<(Take & { kind: 'over' | 'unannounced' }) | null>(null);
  const [lot, setLot] = useState('');
  const [expiry, setExpiry] = useState('');
  const [damaged, setDamaged] = useState(false);
  // print: barcodes just saved with "Barkod ekle", offered for printing until the next message
  const [message, setMessage] = useState<{ tone: 'ok' | 'bad' | 'warn'; text: string; print?: BarcodesToPrint } | null>(null);
  // "Barkodu bas" opened from that message
  const [printing, setPrinting] = useState<BarcodesToPrint | null>(null);
  // a product without a size is measured once, on the first unit; "Sonra" leaves it for this visit
  const [measuring, setMeasuring] = useState<InboundShipmentLine | null>(null);
  const [later, setLater] = useState<ReadonlySet<string>>(new Set());
  // goods with no barcode on them: counted by hand and labelled at the dock
  const [unlabelled, setUnlabelled] = useState<InboundShipmentLine | null>(null);
  // a supervisor takes goods in by count, with no barcode read
  const [byHand, setByHand] = useState<InboundShipmentLine | null>(null);
  const [closing, setClosing] = useState(false);
  // a second tap on a phone does not close twice
  const [closeBusy, setCloseBusy] = useState(false);
  const [report, setReport] = useState<CloseInboundResult | null>(null);
  // a supervisor opens a closed receipt again (a carton found after closing)
  const [reopening, setReopening] = useState(false);
  // counts made outside the scan field (a confirmation, a count by hand, barkodsuz kabul): a lost answer is sent again with its key
  const dock = useFloorAction();
  // a scan whose answer was lost: "geri al" would take back the scan before it
  const [scanHeld, setScanHeld] = useState(false);
  // the accepted lines of a long open shipment start folded; this device remembers a list kept open
  const [acceptedOpen, setAcceptedOpen] = useState(() => store.get(ACCEPTED_KEY) === 'open');

  const current = shipment ?? loaded.data ?? null;
  // a wrong number is no dead end: back to the list, or scan again here
  if (loaded.error && !current) {
    return (
      <>
        <div className="row">
          <a className="btn ghost" href="#/kabul">
            <Icon name="back" /> Gönderiler
          </a>
          <h2 className="mono">{asnNumber}</h2>
        </div>
        <ErrorBanner message={loaded.error} onRetry={() => void loaded.reload()} />
        <AsnScan />
      </>
    );
  }
  if (!current) return <Spinner />;
  const closed = current.status === 'closed' || current.status === 'cancelled';
  // Düzelt: managers on an open receipt, the depo yöneticisi on a closed one too
  const canFix = current.status === 'closed' ? isAdmin : current.status !== 'cancelled' && isSupervisor;

  // a line's new count, onto the shipment as it stands now: a repeat after a lost answer runs with an older screen
  // (an unannounced product taken in is a new line)
  const counted = (line: ReceiveScanResult['line']) =>
    setShipment((now) => {
      const base = now ?? current;
      const lines = base.lines.some((l) => l.id === line.id) ? base.lines.map((l) => (l.id === line.id ? line : l)) : [...base.lines, line];
      return { ...base, status: 'receiving', lines };
    });

  // a count made outside the scan field is not sent while an earlier one is on its way or waits: said so, not lost
  const sendCount = (what: string | ActionWords, send: (action: FloorAction) => Promise<unknown>, note?: string) =>
    void dock.run(what, send).then((sent) => {
      if (sent) return;
      feedback('error');
      setMessage({ tone: 'bad', text: `${note ? `${note} · ` : ''}Gönderilmedi: önceki işlem henüz bitmedi. Bekleyip ${what === SCAN_WORDS ? 'ürünü tekrar okutun' : 'tekrar deneyin'}.` });
    });

  // keep: the count was just made fixed in this same step (the state of this render does not know yet)
  const scan = async (
    barcode: string,
    opts: { confirm?: { acceptOver?: boolean; acceptUnannounced?: boolean }; again?: Take; note?: string; keep?: boolean; action?: FloorAction } = {},
  ) => {
    const take = opts.again ?? { barcode, qty, lotNo: lot.trim(), expiry, damaged };
    // what was done just before (a barcode saved) stays in the message
    const note = opts.note ? `${opts.note} · ` : '';
    setConfirmable(null);
    try {
      const result = await api<ReceiveScanResult>(
        'POST',
        `/api/wms/receiving/${current.id}/scan`,
        {
          ...(take.productId ? { productId: take.productId } : { barcode: take.barcode }),
          ...(take.qty > 1 ? { qty: take.qty } : {}),
          ...(take.lotNo ? { lotNo: take.lotNo } : {}),
          ...(take.expiry ? { expiryDate: take.expiry } : {}),
          ...(take.damaged ? { condition: 'damaged' } : {}),
          ...(take.taught && take.taught.length > 0 ? { taughtBarcodes: take.taught } : {}),
          ...opts.confirm,
        },
        { action: opts.action },
      );
      feedback('ok');
      counted(result.line);
      setMessage({
        tone: 'ok',
        text: `${note}+${result.receipt.qty} ${result.line.productName} → ${result.receipt.locationCode}${
          result.putaway?.suggestions[0] ? ` · yerleştirme: ${result.putaway.suggestions[0].code}` : ''
        }${result.receipt.condition === 'damaged' ? ' (hasarlı ayrıldı)' : ''}`,
      });
      if (!(opts.keep ?? keepQty)) setQty(1);
      setDamaged(false);
      if (!result.line.measured && result.receipt.condition === 'good' && !later.has(result.line.productId)) setMeasuring(result.line);
    } catch (e) {
      feedback('error');
      const code = e instanceof ApiError ? e.code : null;
      // the dock asks in its own words (accept the extra, teach the barcode): no "SAYILMADI" next to it
      if (opts.action && (code === 'OVER_RECEIPT' || code === 'UNANNOUNCED_PRODUCT' || code === 'UNKNOWN_BARCODE' || code === 'CASE_QTY_UNKNOWN')) {
        opts.action.answered = true;
      }
      if (code === 'OVER_RECEIPT' || code === 'UNANNOUNCED_PRODUCT') setConfirmable({ ...take, kind: code === 'OVER_RECEIPT' ? 'over' : 'unannounced' });
      if (code === 'UNKNOWN_BARCODE' || code === 'CASE_QTY_UNKNOWN') {
        // a carton GTIN of a known unit, with no carton size anywhere: the dock says how many are inside
        const details = (e as ApiError).details as (Partial<UnknownBarcodeDetails> & { sku?: string }) | undefined;
        const sku = code === 'CASE_QTY_UNKNOWN' ? details?.sku : undefined;
        const productId = current.lines.find((l) => l.sku === sku)?.productId ?? null;
        setTeach({
          scanned: { barcode: take.barcode, carton: code === 'CASE_QTY_UNKNOWN', productId, qty: take.qty, otherSeller: details?.otherSeller === true },
          again: take,
        });
        setMessage(null);
        return;
      }
      setMessage({ tone: 'bad', text: `${note}${errorMessage(e)}` });
    }
  };

  // geri al: my latest scan on this ASN (units leave the dock again, their putaway is cancelled; a barcode taught for it goes too)
  const undo = async (action?: FloorAction) => {
    try {
      const next = await api<UndoneReceipt>('POST', `/api/wms/receiving/${current.id}/undo-last`, undefined, { action });
      setShipment(next);
      feedback('ok');
      const forgotten = next.forgottenBarcodes.length > 0 ? ` Bu okutma için kaydedilen barkod da silindi: ${next.forgottenBarcodes.join(', ')}.` : '';
      setMessage({ tone: 'warn', text: `Son okutma geri alındı; adetler ve yerleştirme görevi düzeltildi.${forgotten}` });
    } catch (e) {
      feedback('error');
      setMessage({ tone: 'bad', text: errorMessage(e) });
    }
  };

  const expected = current.lines.reduce((n, l) => n + l.expectedQty, 0);
  const received = current.lines.reduce((n, l) => n + l.receivedQty + l.damagedQty, 0);
  // announced lines nothing was counted for: a carton may still be on the truck (not one a manager said will not come)
  const untouched = current.lines.filter((l) => l.expectedQty > 0 && l.receivedQty + l.damagedQty === 0 && l.shortMarkedAt === null);
  // what is still to come apart from what is in; each scan re-renders from the shipment, so a line moves down as it completes
  const lists = splitLines(current.lines);
  const folds = acceptedFolds(!closed, lists.accepted.length);
  const folded = folds && !acceptedOpen;
  const toggleAccepted = () => {
    setAcceptedOpen(folded);
    store.set(ACCEPTED_KEY, folded ? 'open' : 'closed');
  };

  // a line in either list: its counts, its chips and what can be done with it
  const lineRow = (l: InboundShipmentLine) => {
    const diff = l.receivedQty + l.damagedQty - l.expectedQty;
    // a manager said the rest will not come: no longer expected
    const shortMarked = l.shortMarkedAt !== null && diff < 0;
    return (
      <div key={l.id} className="rcv-line">
        <div style={{ minWidth: 0 }}>
          <b>{l.productName}</b>
          <div className="mono muted" style={{ fontSize: 12 }}>
            {l.sku}
            {l.packagingLevel !== 'each' &&
              ` · ${l.expectedQty / l.unitsPerPack} ${PACKAGING_LEVEL_LABELS[l.packagingLevel].toLocaleLowerCase('tr-TR')} × ${l.unitsPerPack}`}
          </div>
        </div>
        <div className="rcv-count" title="Gelen / beklenen">
          <b>{fmt.n(l.receivedQty)}</b> / {fmt.n(l.expectedQty)}
          {shortMarked ? (
            <span className="chip bad" title="Yönetici kalanın gelmeyeceğini işaretledi">
              Eksik geldi: {fmt.n(l.receivedQty + l.damagedQty)}/{fmt.n(l.expectedQty)}
            </span>
          ) : (
            diff !== 0 && l.receivedQty + l.damagedQty > 0 && <span className={`chip ${diff > 0 ? 'warn' : ''}`}>{diff > 0 ? `+${diff}` : diff}</span>
          )}
          {l.damagedQty > 0 && <div className="muted">{fmt.n(l.damagedQty)} hasarlı</div>}
        </div>
        {closed && canFix && (
          <div className="line-actions">
            <button className="btn sm" title="Gelen adedi, hasarlıları ve okutmaları düzeltin" onClick={() => setFixing(l)}>
              <Icon name="edit" size={16} /> Düzelt
            </button>
          </div>
        )}
        {!closed && (
          <div className="line-actions">
            {canFix && (
              <button className="btn sm" title="Gelen adedi düzeltin, eksik işaretleyin, okutma silin" onClick={() => setFixing(l)}>
                <Icon name="edit" size={16} /> Düzelt
              </button>
            )}
            {!l.measured && (
              <button className="chip warn select" onClick={() => setMeasuring(l)}>
                <Icon name="ruler" size={14} /> Ölçü yok · ölç
              </button>
            )}
            {isSupervisor && (
              <button className="btn sm" title="Barkod okutmadan, adedi yazarak kabul edin" onClick={() => setByHand(l)}>
                <Icon name="check" size={16} /> Okutmadan kabul
              </button>
            )}
            <button className="btn sm" title="Ürünün ya da kolisinin üstündeki barkodu bu ürüne kaydedin" onClick={() => setTeach({ line: l })}>
              <Icon name="scan" size={16} /> Barkod ekle
            </button>
            <button
              className="btn sm"
              title="Üzerinde barkod olmayan ürün: sayıp kabul edin, etiketini basın"
              disabled={dock.waiting}
              onClick={() => setUnlabelled(l)}
            >
              <Icon name="printer" size={16} /> Barkodsuz kabul
            </button>
          </div>
        )}
      </div>
    );
  };

  return (
    // display: contents; the scan bar sticks over the whole screen, not inside a card
    <div className="rcv-screen">
      <div className="row rcv-head">
        <a className="btn ghost" href="#/kabul">
          <Icon name="back" /> Gönderiler
        </a>
        <h2 className="mono">{current.asnNumber}</h2>
        <SellerChip code={current.tenantCode} />
        {current.reference && <span className="muted">{current.reference}</span>}
        <span style={{ flex: 1 }} />
        <b>
          {fmt.n(received)} / {fmt.n(expected)} adet
        </b>
      </div>
      <div className="progress">
        <i style={{ width: `${expected ? Math.min(100, (received / expected) * 100) : 0}%` }} />
      </div>
      {/* the scan field and what the last scan did stay in view while the list scrolls */}
      <div className="rcv-sticky">
        {!closed && (
          <ScanField
            placeholder="Ürün, koli (ITF-14) veya GS1-128 etiketini okutun"
            onScan={(code, action) => scan(code, { action })}
            // a manager typing: the shipment's seller's products (a pick counts as one scan)
            suggest={{ kinds: ['product'], tenantId: current.tenantId }}
            onHeld={setScanHeld}
            disabled={measuring !== null}
            hint={
              measuring ? (
                'Önce ölçüyü kaydedin ya da “Sonra” deyin.'
              ) : qty > 1 ? (
                <b>
                  Okutma ×{qty} sayılır{keepQty ? ' (sabit)' : ''}
                </b>
              ) : current.lines.some((l) => l.trackLot || l.trackExpiry) ? (
                'GS1-128 etiketindeki lot ve SKT otomatik okunur; diğerlerinde aşağıya girin.'
              ) : undefined
            }
          />
        )}
        {message && (
          <div className={`banner ${message.tone}`}>
            <div style={{ flex: 1, minWidth: 0 }}>{message.text}</div>
            {message.print && (
              <button className="btn sm" onClick={() => setPrinting(message.print!)} title="Kaydedilen barkodun etiketini basar">
                <Icon name="printer" size={16} /> Barkodu bas
              </button>
            )}
          </div>
        )}
        {/* barkodsuz kabul shows it in its dialog while open */}
        {!unlabelled && dock.banner}
      </div>
      {teach && (
        <BarcodeDialog
          shipmentId={current.id}
          lines={current.lines}
          line={teach.line}
          scanned={teach.scanned}
          onClose={() => {
            // the unknown scan stays uncounted: said, so nobody takes it as counted
            if (teach.scanned) setMessage({ tone: 'warn', text: `${teach.scanned.barcode} sayılmadı.` });
            setTeach(null);
            focusScanField();
          }}
          onSetAside={(barcode) => {
            setTeach(null);
            focusScanField();
            setMessage({ tone: 'warn', text: `${barcode} sayılmadı. Ürünü sorunlu rafına koyun, sorumluya haber verin.` });
          }}
          onTaught={(taught, perScan) => {
            const again = teach.again;
            setTeach(null);
            focusScanField();
            // a carton with no barcode: every scan of the product counts the carton until changed
            if (perScan) {
              setQty(perScan);
              setKeepQty(true);
            }
            const note = [taught ? taughtText(taught) : null, perScan ? `bir okutma ${perScan} adet sayar (sabit)` : null].filter(Boolean).join(' · ');
            // the scan nobody knew counts now (a whole carton when its count was given); the codes saved for it go with it on geri al
            const added = taught?.added.map((b) => b.barcode) ?? [];
            if (again)
              sendCount(
                SCAN_WORDS,
                (action) => scan(again.barcode, { again: { ...again, ...(perScan ? { qty: perScan } : {}), taught: added }, note, ...(perScan ? { keep: true } : {}), action }),
                note,
              );
            else {
              // opened from a line ("Barkod ekle"): the codes saved can go on labels now
              const codes = teach.line && taught ? taughtCodes(taught) : [];
              setMessage({ tone: 'ok', text: note || 'Kaydedildi', ...(teach.line && codes.length > 0 ? { print: { line: teach.line, codes } } : {}) });
            }
          }}
        />
      )}
      {confirmable && (
        <div className="banner warn" style={{ flexWrap: 'wrap' }}>
          <Icon name="alert" />
          <div style={{ flex: 1, minWidth: 200 }}>
            {confirmable.kind === 'over'
              ? 'Beklenenden fazla geldi. Fazla adet gerçekten geldiyse kabul edin; satıcının fark tutanağında “fazla” görünür.'
              : 'Bu ürün gönderide yok. Gerçekten geldiyse kabul edin; gönderiye satır eklenir, fark tutanağında “fazla” görünür.'}
          </div>
          <div className="row rcv-actions">
            <button
              className="btn primary"
              disabled={dock.waiting}
              onClick={() =>
                void dock.run(confirmable.kind === 'over' ? 'Fazlayı da kabul et' : 'Yine de kabul et', (action) =>
                  scan(confirmable.barcode, { confirm: confirmable.kind === 'over' ? { acceptOver: true } : { acceptUnannounced: true }, again: confirmable, action }),
                )
              }
            >
              {confirmable.kind === 'over' ? 'Fazlayı da kabul et' : 'Yine de kabul et'}
            </button>
            <button className="btn" onClick={() => setConfirmable(null)}>
              Kabul etme
            </button>
          </div>
        </div>
      )}

      {/* closed here, now (an old receipt opened again is not a call to put away) */}
      {shipment?.status === 'closed' && (
        <NextStep
          title="Ürünleri yerleştirin"
          steps={['Kabul alanındaki ürünleri raflara koyun: ürün rafa girince siparişlere ayrılabilir.']}
          go={{ label: 'Yerleştirmeye geç', to: '/yerlestirme', icon: 'shelf' }}
        />
      )}
      <div className="split">
        <section className="card">
          <div className="card-b stack">
            {closed ? (
              <>
                <div className="banner info">
                  {current.status === 'cancelled'
                    ? 'Bu bildirim iptal edilmiş.'
                    : isAdmin
                      ? 'Bu bildirim kapatılmış. Sayı yanlışsa kalemdeki “Düzelt” ile yeniden açmadan düzeltin: satıcıya düzeltilmiş sonuç bildirilir.'
                      : isSupervisor
                        ? 'Bu bildirim kapatılmış. Sayıyı düzeltmek için kabulü yeniden açın ya da depo yöneticisine haber verin.'
                        : 'Bu bildirim kapatılmış. Sonradan koli bulunursa vardiya sorumlusuna haber verin; kabulü o yeniden açar.'}
                </div>
                {current.status === 'closed' &&
                  isSupervisor &&
                  (current.receivingInvoiced ? (
                    <span className="muted">Mal kabul ücreti faturalandı; kabul yeniden açılamaz.</span>
                  ) : (
                    <div className="row rcv-actions">
                      <button className="btn" onClick={() => setReopening(true)}>
                        <Icon name="undo" /> Kabulü yeniden aç
                      </button>
                    </div>
                  ))}
              </>
            ) : (
              <>
                {measuring && (
                  <MeasureCard
                    line={measuring}
                    onSaved={(desi) => {
                      setShipment({ ...current, lines: current.lines.map((l) => (l.productId === measuring.productId ? { ...l, measured: true } : l)) });
                      setMessage({ tone: 'ok', text: `${measuring.productName}: ölçü kaydedildi${desi === null ? '' : ` · ${fmt.n(desi)} desi`}` });
                      setMeasuring(null);
                    }}
                    onLater={() => {
                      setLater(new Set([...later, measuring.productId]));
                      setMeasuring(null);
                    }}
                  />
                )}
                <PerScanCount
                  qty={qty}
                  keep={keepQty}
                  onQty={setQty}
                  onKeep={setKeepQty}
                  onScan={(code, n) => sendCount(SCAN_WORDS, (action) => scan(code, { again: { barcode: code, qty: n, lotNo: lot.trim(), expiry, damaged }, action }))}
                />
                {current.lines.some((l) => l.trackLot || l.trackExpiry) && (
                  <div className="row" style={{ alignItems: 'flex-end' }}>
                    <div className="field" style={{ flex: 1, minWidth: 140 }}>
                      <label htmlFor="lot">Lot</label>
                      <input id="lot" className="input mono" value={lot} onChange={(e) => setLot(e.target.value.toUpperCase())} placeholder="Gerekiyorsa" />
                    </div>
                    <div className="field" style={{ flex: 1, minWidth: 160 }}>
                      <label htmlFor="expiry">Son kullanma tarihi</label>
                      <input id="expiry" className="input" type="date" value={expiry} onChange={(e) => setExpiry(e.target.value)} />
                    </div>
                  </div>
                )}
                <label className="row rcv-check">
                  <input type="checkbox" checked={damaged} onChange={(e) => setDamaged(e.target.checked)} />
                  Hasarlı (hasarlı alanına ayrılır, satışa açılmaz)
                </label>
                <div className="row rcv-actions">
                  <button
                    className="btn"
                    onClick={() => void dock.run('Son okutmayı geri al', undo)}
                    disabled={current.status !== 'receiving' || dock.waiting || scanHeld}
                    title={scanHeld ? 'Önce bağlantısı kopan okutmanın sonucu belli olmalı' : undefined}
                  >
                    <Icon name="undo" /> Son okutmayı geri al
                  </button>
                  <button className="btn" onClick={() => setClosing(true)}>
                    Kabulü kapat
                  </button>
                </div>
              </>
            )}
          </div>
        </section>
        {/* what is still to come above what is in; lists, not tables: on a phone the counts and the buttons stay in view */}
        <div className="stack">
          <section className="card">
            <div className="card-h">
              <h2>Kalanlar</h2>
              {lists.remaining.length > 0 && (
                <span className="sub">
                  {fmt.n(lists.remaining.length)} kalem · {fmt.n(lists.remainingUnits)} adet {closed ? 'gelmedi' : 'bekleniyor'}
                </span>
              )}
            </div>
            {lists.remaining.length > 0 ? (
              <div className="rcv-lines">{lists.remaining.map(lineRow)}</div>
            ) : (
              <div className="card-b muted">Kalan kalem yok — hepsi kabul edildi.</div>
            )}
          </section>
          <section className="card">
            <div className="card-h">
              <h2>Kabul edilenler</h2>
              {lists.accepted.length > 0 && (
                <span className="sub">
                  {fmt.n(lists.accepted.length)} kalem · {fmt.n(lists.acceptedUnits)} adet
                </span>
              )}
              {folds && (
                <>
                  <span className="spacer" />
                  <button className="btn sm" aria-expanded={!folded} onClick={toggleAccepted}>
                    {folded ? `Göster (${fmt.n(lists.accepted.length)} kalem)` : 'Gizle'}
                  </button>
                </>
              )}
            </div>
            {lists.accepted.length === 0 ? (
              <div className="card-b muted">{closed ? 'Kabul edilen kalem yok.' : 'Henüz kabul edilen kalem yok.'}</div>
            ) : (
              !folded && <div className="rcv-lines">{lists.accepted.map(lineRow)}</div>
            )}
          </section>
        </div>
      </div>

      {isSupervisor && <CorrectionsCard shipmentId={current.id} version={corrected} />}

      {fixing && (
        <LineFixDialog
          shipment={current}
          line={current.lines.find((l) => l.id === fixing.id) ?? fixing}
          onClose={() => setFixing(null)}
          onFixed={(result, text) => {
            setShipment(result.shipment);
            setCorrected((n) => n + 1);
            feedback('ok');
            setMessage({ tone: 'ok', text });
            // the line is gone (an unannounced one with nothing left): the dialog with it
            if (!result.shipment.lines.some((l) => l.id === fixing.id)) setFixing(null);
          }}
        />
      )}
      {printing && (
        <ProductLabelDialog
          // the code is chosen in the dialog (the one just added first): the title names the product
          title={`Barkodu bas · ${printing.line.sku}`}
          products={[
            {
              id: printing.line.productId,
              sku: printing.line.sku,
              name: printing.line.productName,
              barcode: printing.codes[0]!.barcode,
              codes: printing.codes,
              sellerCode: current.tenantCode,
              trackLot: printing.line.trackLot,
              trackExpiry: printing.line.trackExpiry,
              unitLabels: printing.line.unitLabels,
              physical: 0,
            },
          ]}
          lotNo={lot.trim()}
          expiryDate={expiry}
          onClose={() => setPrinting(null)}
        />
      )}
      {byHand && (
        <HandReceiveDialog
          line={byHand}
          lotNo={lot.trim()}
          expiry={expiry}
          onClose={() => setByHand(null)}
          onReceive={(take) => {
            setByHand(null);
            sendCount('Okutmadan kabul', (action) => scan(take.barcode, { again: take, note: 'Okutmadan kabul', action }));
          }}
        />
      )}
      {unlabelled && (
        <NoBarcodeDialog
          shipmentId={current.id}
          sellerCode={current.tenantCode}
          line={unlabelled}
          runner={dock}
          onClose={() => setUnlabelled(null)}
          onReceived={(result, labels) => {
            setUnlabelled(null);
            counted(result.line);
            setMessage({
              tone: labels.error ? 'warn' : 'ok',
              text: `+${result.receipt.qty} ${result.line.productName} → ${result.receipt.locationCode}${
                result.putaway?.suggestions[0] ? ` · yerleştirme: ${result.putaway.suggestions[0].code}` : ''
              }${labels.printed > 0 ? ` · ${labels.printed} etiket yazdırıldı, ürünlere yapıştırın` : ''}${labels.error ? ` · etiket basılamadı: ${labels.error}` : ''}`,
            });
            if (!result.line.measured && result.receipt.condition === 'good' && !later.has(result.line.productId)) setMeasuring(result.line);
          }}
        />
      )}
      {closing && (
        <Modal
          title="Mal kabul kapatılsın mı?"
          onClose={() => setClosing(false)}
          footer={
            <>
              <button className="btn" onClick={() => setClosing(false)}>
                Vazgeç
              </button>
              <button
                className="btn primary"
                disabled={closeBusy}
                onClick={async () => {
                  setCloseBusy(true);
                  try {
                    const result = await api<CloseInboundResult>('POST', `/api/wms/receiving/${current.id}/close`);
                    setReport(result);
                    setShipment({ ...current, status: 'closed' });
                  } catch (e) {
                    setMessage({ tone: 'bad', text: errorMessage(e) });
                  }
                  setClosing(false);
                  setCloseBusy(false);
                }}
              >
                {untouched.length > 0 ? 'Yine de kapat' : 'Kapat ve tutanak oluştur'}
              </button>
            </>
          }
        >
          <div className="stack rcv-dialog">
            {untouched.length > 0 && (
              <div className="banner bad" style={{ display: 'block' }}>
                <b>Bütün koliler indirildi mi?</b> Hiç okutulmamış {untouched.length === 1 ? 'bir kalem' : `${untouched.length} kalem`} var:{' '}
                {untouched
                  .slice(0, 4)
                  .map((l) => l.productName)
                  .join(', ')}
                {untouched.length > 4 ? ` ve ${untouched.length - 4} kalem daha` : ''}. Araçta ya da rampada koli kalmadığına bakın.
              </div>
            )}
            {(() => {
              const off = current.lines
                .map((l) => ({ l, diff: l.receivedQty + l.damagedQty - l.expectedQty }))
                .filter((x) => x.diff !== 0);
              return off.length === 0 ? (
                <div className="banner ok">Bütün kalemler beklenen adette geldi.</div>
              ) : (
                <div className="banner warn" style={{ display: 'block' }}>
                  <b>Fark tutanağına yazılacaklar:</b>
                  <ul style={{ margin: '6px 0 0', paddingLeft: 18 }}>
                    {off.map(({ l, diff }) => (
                      <li key={l.id}>
                        {l.sku} · {l.productName}: beklenen {fmt.n(l.expectedQty)}, gelen {fmt.n(l.receivedQty + l.damagedQty)} →{' '}
                        <b>{diff < 0 ? `${fmt.n(-diff)} eksik` : `${fmt.n(diff)} fazla`}</b>
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })()}
            <p style={{ margin: 0 }}>
              Kapatınca eksik ve fazla adetler satıcının paneline fark tutanağı olarak düşer, mal kabul ücreti satıcının hizmet ücretlerine yazılır. Eksik
              kalan ürünler sonra gelirse satıcı yeni bir gönderi oluşturur. Kapattıktan sonra bulunan koli için vardiya sorumlusu kabulü yeniden açar.
            </p>
          </div>
        </Modal>
      )}
      {reopening && (
        <ReopenDialog
          shipment={current}
          onClose={() => setReopening(false)}
          onReopened={(next) => {
            setReopening(false);
            setReport(null);
            setShipment(next);
            setMessage({ tone: 'ok', text: 'Kabul yeniden açıldı. Bulunan kolileri okutun, sonra kabulü yeniden kapatın.' });
          }}
        />
      )}
      {report && (
        <Modal
          title={`${report.asnNumber} kapatıldı`}
          onClose={() => setReport(null)}
          footer={
            <button className="btn primary" onClick={() => navigate('/yerlestirme')}>
              Yerleştirmeye geç
            </button>
          }
        >
          <div className="stack">
            <div className="row">
              <span className="chip">Beklenen {report.totals.expected}</span>
              <span className="chip ok">Gelen {report.totals.received}</span>
              {report.totals.damaged > 0 && <span className="chip bad">Hasarlı {report.totals.damaged}</span>}
            </div>
            {report.discrepancies.length === 0 ? (
              <div className="banner ok">Fark yok.</div>
            ) : (
              <table className="t">
                <tbody>
                  {report.discrepancies.map((d) => (
                    <tr key={d.lineNo}>
                      <td className="mono">{d.sku}</td>
                      <td className="num">{d.difference > 0 ? `+${d.difference} fazla` : `${-d.difference} eksik`}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </Modal>
      )}
    </div>
  );
}

/**
 * Kabulü yeniden aç: a closed receipt goes back to the dock (a carton found after closing, a count
 * to correct). The receiving charge booked at close goes and is booked anew when it closes again;
 * once invoiced the API refuses. Supervisors only (the API checks the role).
 */
function ReopenDialog(props: { shipment: InboundShipment; onClose: () => void; onReopened: (shipment: InboundShipment) => void }) {
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const reopen = async () => {
    setBusy(true);
    setError(null);
    try {
      props.onReopened(await api<InboundShipment>('POST', `/api/wms/receiving/${props.shipment.id}/reopen`));
    } catch (e) {
      feedback('error');
      setError(errorMessage(e));
      setBusy(false);
    }
  };
  return (
    <Modal
      title={`${props.shipment.asnNumber} yeniden açılsın mı?`}
      onClose={props.onClose}
      footer={
        <>
          <button className="btn" onClick={props.onClose}>
            Vazgeç
          </button>
          <button className="btn primary" disabled={busy} onClick={() => void reopen()}>
            <Icon name="undo" /> Kabulü yeniden aç
          </button>
        </>
      }
    >
      <div className="stack rcv-dialog">
        {error && <div className="banner bad">{error}</div>}
        <p style={{ margin: 0 }}>Kapattıktan sonra bulunan koliler okutulabilir, yanlış sayım düzeltilebilir. Okutulanlar ve yerleştirme görevleri olduğu gibi kalır.</p>
        <p className="muted" style={{ margin: 0 }}>
          Kapatırken yazılan mal kabul ücreti silinir; kabul yeniden kapatılınca güncel adetle bir kez yazılır. Ücret faturalandıysa kabul açılamaz.
        </p>
      </div>
    </Modal>
  );
}

/** What a correction did, for the dock's message: "ELD-1 · Adet düzeltildi: 3 sağlam → 2 sağlam (1 eksik) · KB-01-1-1: 1 adet çıkarıldı" */
const fixedText = (c: InboundCorrectionView) =>
  [`${c.sku} · ${INBOUND_CORRECTION_LABELS[c.kind]}: ${correctionSummary(c)}`, c.moves.length > 0 ? movesText(c.moves) : null, c.shipmentClosed ? 'satıcıya bildirildi' : null]
    .filter(Boolean)
    .join(' · ');

type FixAsk = { readonly kind: 'receipt'; readonly receipt: InboundReceiptView } | { readonly kind: 'line' } | { readonly kind: 'counts' };

/**
 * Düzelt: a manager corrects what a line received without closing the receipt (the depo yöneticisi a
 * closed one too): the good and damaged counts set directly, "eksik geldi", one scan or the whole line
 * deleted. Units not put away leave the dock; units on a shelf leave their bin (depo yöneticisi only,
 * never ones orders hold). Each step says what it will do and asks once more; the API refuses what the
 * role may not do, with the reason.
 */
function LineFixDialog(props: {
  shipment: InboundShipment;
  line: InboundShipmentLine;
  onClose: () => void;
  onFixed: (result: InboundCorrectionResult, text: string) => void;
}) {
  const { isAdmin } = useSession();
  const { shipment, line } = props;
  const isClosed = shipment.status === 'closed';
  const receipts = useLoad(() => api<InboundReceiptView[]>('GET', `/api/wms/receiving/${shipment.id}/receipts`), [shipment.id, line.receivedQty, line.damagedQty]);
  const mine = (receipts.data ?? []).filter((r) => r.lineId === line.id && r.qty - r.removedQty > 0);
  const places = mine.flatMap((r) => r.places);
  // the boxes start from the line as it is, and again after each correction
  const [shown, setShown] = useState({ received: line.receivedQty, damaged: line.damagedQty });
  const [good, setGood] = useState(String(line.receivedQty));
  const [damaged, setDamaged] = useState(String(line.damagedQty));
  if (shown.received !== line.receivedQty || shown.damaged !== line.damagedQty) {
    setShown({ received: line.receivedQty, damaged: line.damagedQty });
    setGood(String(line.receivedQty));
    setDamaged(String(line.damagedQty));
  }
  const [lot, setLot] = useState(line.lotNo ?? '');
  const [expiry, setExpiry] = useState(line.expiryDate ?? '');
  const [note, setNote] = useState('');
  const [asking, setAsking] = useState<FixAsk | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const next = { good: good === '' ? NaN : Number(good), damaged: damaged === '' ? NaN : Number(damaged) };
  const valid = Number.isInteger(next.good) && Number.isInteger(next.damaged) && next.good >= 0 && next.damaged >= 0;
  const preview = valid ? fixPreview(line, places, next) : null;
  const adding = valid && (next.good > line.receivedQty || next.damaged > line.damagedQty);
  // an invoiced receiving charge is never rewritten: the good count stays
  const goodLocked = isClosed && shipment.receivingInvoiced;
  const counted = line.receivedQty + line.damagedQty;
  const isShort = counted < line.expectedQty;
  const noteText = note.trim() || null;

  const send = async (path: string, body: unknown) => {
    setBusy(true);
    setError(null);
    try {
      const result = await api<InboundCorrectionResult>('POST', `/api/wms/receiving/${shipment.id}${path}`, body);
      setAsking(null);
      setNote('');
      void receipts.reload();
      props.onFixed(result, fixedText(result.correction));
    } catch (e) {
      feedback('error');
      setError(errorMessage(e));
      // a scan came in meanwhile: the figures are fetched again with the receipts
      if (e instanceof ApiError && e.code === 'LINE_CHANGED') void receipts.reload();
    }
    setBusy(false);
  };
  const saveCounts = () =>
    void send(`/lines/${line.id}/quantity`, {
      receivedQty: next.good,
      damagedQty: next.damaged,
      shownReceivedQty: line.receivedQty,
      shownDamagedQty: line.damagedQty,
      ...(adding && lot.trim() ? { lotNo: lot.trim() } : {}),
      ...(adding && expiry ? { expiryDate: expiry } : {}),
      ...(noteText ? { note: noteText } : {}),
    });
  const removeReceipt = (r: InboundReceiptView) => void send(`/receipts/${r.id}/remove`, noteText ? { note: noteText } : {});
  const removeLine = () =>
    void send(`/lines/${line.id}/remove`, { shownReceivedQty: line.receivedQty, shownDamagedQty: line.damagedQty, ...(noteText ? { note: noteText } : {}) });
  const markShort = (short: boolean) => void send(`/lines/${line.id}/short`, { short, ...(noteText ? { note: noteText } : {}) });

  const box = (id: string, label: string, value: string, onChange: (v: string) => void, disabled = false) => (
    <div className="field" style={{ flex: 1, minWidth: 110 }}>
      <label htmlFor={id}>{label}</label>
      <input
        id={id}
        className="input"
        inputMode="numeric"
        autoComplete="off"
        disabled={disabled || busy}
        value={value}
        onFocus={(e) => e.target.select()}
        onChange={(e) => {
          onChange(e.target.value.replace(/\D/g, '').slice(0, 7));
          setAsking(null);
        }}
      />
    </div>
  );

  return (
    <Modal title={`${line.sku} · düzelt`} onClose={() => !busy && props.onClose()} wide>
      <div className="stack rcv-dialog">
        <p style={{ margin: 0 }}>
          <b>{line.productName}</b>: beklenen {fmt.n(line.expectedQty)}, gelen {countsText({ received: line.receivedQty, damaged: line.damagedQty })}
          {line.shortMarkedAt && isShort && <span className="chip bad" style={{ marginLeft: 6 }}>Eksik geldi</span>}
        </p>
        {isClosed && (
          <div className="banner info-soft">
            Kabul kapatılmış. Düzeltme kabulü yeniden açmadan yapılır: satıcıya düzeltilmiş sonuç bildirilir
            {goodLocked ? '. Mal kabul ücreti faturalandığı için sağlam adet değiştirilemez; hasarlı adet düzeltilebilir.' : ', mal kabul ücreti yeni sağlam adetle yeniden yazılır.'}
          </div>
        )}
        {error && <div className="banner bad">{error}</div>}

        <section className="fix-part">
          <h3>Gelen adet</h3>
          <div className="row" style={{ alignItems: 'flex-end' }}>
            {box('fix-good', 'Sağlam', good, setGood, goodLocked)}
            {box('fix-damaged', 'Hasarlı', damaged, setDamaged)}
          </div>
          {adding && (line.trackLot || line.trackExpiry) && (
            <div className="row" style={{ alignItems: 'flex-end' }}>
              {line.trackLot && (
                <div className="field" style={{ flex: 1, minWidth: 140 }}>
                  <label htmlFor="fix-lot">Eklenenlerin lotu</label>
                  <input id="fix-lot" className="input mono" value={lot} onChange={(e) => setLot(e.target.value.toUpperCase())} />
                </div>
              )}
              {line.trackExpiry && (
                <div className="field" style={{ flex: 1, minWidth: 160 }}>
                  <label htmlFor="fix-expiry">Eklenenlerin son kullanma tarihi</label>
                  <input id="fix-expiry" className="input" type="date" value={expiry} onChange={(e) => setExpiry(e.target.value)} />
                </div>
              )}
            </div>
          )}
          {preview?.changed && (
            <div className={`banner ${preview.fromShelf > 0 && !isAdmin ? 'warn' : 'info-soft'}`} style={{ display: 'block' }}>
              <ul style={{ margin: 0, paddingLeft: 18 }}>
                {preview.steps.map((s) => (
                  <li key={s}>{s}</li>
                ))}
              </ul>
              <div style={{ marginTop: 4 }}>
                Sonuç: <b>{preview.result}</b>
              </div>
              {preview.fromShelf > 0 && !isAdmin && <div style={{ marginTop: 4 }}>Raftan ürün çıkarmayı yalnız depo yöneticisi yapabilir.</div>}
            </div>
          )}
          {asking?.kind === 'counts' ? (
            <div className="row rcv-actions">
              <button className="btn" disabled={busy} onClick={() => setAsking(null)}>
                Vazgeç
              </button>
              <button className="btn primary" disabled={busy} onClick={saveCounts}>
                Evet, {countsText({ received: next.good, damaged: next.damaged })} olarak kaydet
              </button>
            </div>
          ) : (
            <div className="row rcv-actions">
              <button className="btn primary" disabled={!preview?.changed || busy || receipts.data === undefined} onClick={() => setAsking({ kind: 'counts' })}>
                <Icon name="check" /> Adedi düzelt
              </button>
            </div>
          )}
        </section>

        {!isClosed && (isShort || line.shortMarkedAt) && (
          <section className="fix-part">
            <h3>Eksik geldi</h3>
            {line.shortMarkedAt && isShort ? (
              <>
                <p className="muted" style={{ margin: 0 }}>
                  Kalan {fmt.n(line.expectedQty - counted)} adedin gelmeyeceği işaretlendi. Gelirse işareti kaldırıp okutun.
                </p>
                <div className="row rcv-actions">
                  <button className="btn" disabled={busy} onClick={() => markShort(false)}>
                    <Icon name="undo" /> Eksik işaretini kaldır
                  </button>
                </div>
              </>
            ) : (
              <>
                <p className="muted" style={{ margin: 0 }}>
                  {fmt.n(line.expectedQty)} adet bekleniyordu, {fmt.n(counted)} geldi. Kalan {fmt.n(line.expectedQty - counted)} adet gelmeyecekse işaretleyin: kabul açık
                  kalır, kalem tamam sayılır; kapatınca fark tutanağına “eksik” yazılır.
                </p>
                <div className="row rcv-actions">
                  <button className="btn" disabled={busy} onClick={() => markShort(true)}>
                    <Icon name="alert" /> Eksik geldi: {fmt.n(counted)}/{fmt.n(line.expectedQty)}
                  </button>
                </div>
              </>
            )}
          </section>
        )}

        <section className="fix-part">
          <h3>Okutmalar</h3>
          {receipts.error && <ErrorBanner message={receipts.error} onRetry={() => void receipts.reload()} />}
          {!receipts.data ? (
            <Spinner />
          ) : mine.length === 0 ? (
            <p className="muted" style={{ margin: 0 }}>
              Bu kalemde okutma yok.
            </p>
          ) : (
            <div className="fix-receipts">
              {mine.map((r) => {
                const left = r.qty - r.removedQty;
                const shelf = r.places.filter((p) => p.kind === 'bin');
                return (
                  <div key={r.id} className="fix-receipt">
                    <div style={{ minWidth: 0 }}>
                      <b>{fmt.n(left)} adet</b>
                      {r.removedQty > 0 && <span className="muted"> ({fmt.n(r.qty)} sayıldı, {fmt.n(r.removedQty)} çıkarıldı)</span>}{' '}
                      {r.condition === 'damaged' && <span className="chip bad">hasarlı</span>} {r.byCorrection && <span className="chip">düzeltmeyle eklendi</span>}
                      <div className="muted" style={{ fontSize: 12 }}>
                        {fmt.dateTime(r.receivedAt)} · {r.receivedBy ?? '—'} · {r.scannedCode ? <span className="mono">{r.scannedCode}</span> : 'okutmadan'}
                        {r.lotNo && ` · Lot ${r.lotNo}`}
                        {r.expiryDate && ` · SKT ${fmt.date(r.expiryDate)}`}
                      </div>
                      <div className="muted" style={{ fontSize: 12 }}>
                        {placesText(r.places)}
                      </div>
                      {asking?.kind === 'receipt' && asking.receipt.id === r.id && (
                        <div className="banner warn" style={{ display: 'block', marginTop: 8 }}>
                          Bu okutmanın {fmt.n(left)} adedi stoktan çıkarılır ({placesText(r.places)}).
                          {shelf.length > 0 && !isAdmin && ' Raftaki ürünü yalnız depo yöneticisi çıkarabilir.'} Silinsin mi?
                          <div className="row rcv-actions" style={{ marginTop: 8 }}>
                            <button className="btn" disabled={busy} onClick={() => setAsking(null)}>
                              Vazgeç
                            </button>
                            <button className="btn danger" disabled={busy || (goodLocked && r.condition === 'good')} onClick={() => removeReceipt(r)}>
                              <Icon name="trash" /> Evet, okutmayı sil
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                    {!(asking?.kind === 'receipt' && asking.receipt.id === r.id) && (
                      <button
                        className="btn sm"
                        disabled={busy || (goodLocked && r.condition === 'good')}
                        title="Bu okutmanın ürünlerini stoktan çıkarır"
                        onClick={() => setAsking({ kind: 'receipt', receipt: r })}
                      >
                        <Icon name="trash" size={16} /> Sil
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          )}
          {places.some((p) => p.kind === 'bin') &&
            (isAdmin ? (
              <a className="btn sm ghost" href={`#/harita?q=${encodeURIComponent(line.sku)}`} style={{ alignSelf: 'flex-start' }}>
                <Icon name="map" size={16} /> Raftaki ürünü başka göze taşı (Depo haritası)
              </a>
            ) : (
              <p className="muted" style={{ margin: 0, fontSize: 13 }}>
                Raftaki ürünü başka göze depo yöneticisi Depo haritasından taşır.
              </p>
            ))}
        </section>

        {counted > 0 && !goodLocked && (
          <section className="fix-part">
            <h3>Kalemi sil</h3>
            {asking?.kind === 'line' ? (
              <div className="banner warn" style={{ display: 'block' }}>
                Bu kalemde sayılan {fmt.n(counted)} adedin hepsi stoktan çıkarılır ({placesText(places)}).{' '}
                {line.expectedQty === 0 ? 'Gönderide olmayan bu kalem listeden kalkar.' : `Kalem beklenen ${fmt.n(line.expectedQty)} adetle 0 olarak kalır.`} Emin misiniz?
                <div className="row rcv-actions" style={{ marginTop: 8 }}>
                  <button className="btn" disabled={busy} onClick={() => setAsking(null)}>
                    Vazgeç
                  </button>
                  <button className="btn danger" disabled={busy} onClick={removeLine}>
                    <Icon name="trash" /> Evet, kalemi sil
                  </button>
                </div>
              </div>
            ) : (
              <div className="row rcv-actions">
                <button className="btn" disabled={busy} onClick={() => setAsking({ kind: 'line' })}>
                  <Icon name="trash" /> Kalemdeki bütün ürünleri sil
                </button>
              </div>
            )}
          </section>
        )}

        <div className="field">
          <label htmlFor="fix-note">Not (isteğe bağlı, satıcı da görür)</label>
          <input id="fix-note" className="input" maxLength={500} value={note} placeholder="ör. Tedarikçi eksik gönderdi" onChange={(e) => setNote(e.target.value)} />
        </div>
      </div>
    </Modal>
  );
}

/** The corrections made on a shipment, newest first: who, what and why (the seller sees them too, without who). */
function CorrectionsCard({ shipmentId, version }: { shipmentId: string; version: number }) {
  const list = useLoad(() => api<InboundCorrectionView[]>('GET', `/api/inbound/shipments/${shipmentId}/corrections`), [shipmentId, version]);
  const items = [...(list.data ?? [])].reverse();
  if (items.length === 0) return null;
  return (
    <section className="card">
      <div className="card-h">
        <h2>Düzeltmeler</h2>
        <span className="sub">{items.length} düzeltme · satıcı da görür</span>
      </div>
      <div className="table-wrap">
        <table className="t stack-sm">
          <tbody>
            {items.map((c) => (
              <tr key={c.id}>
                <td className="muted" data-label="Zaman">
                  {fmt.dateTime(c.correctedAt)}
                </td>
                <td>
                  <b>{c.sku}</b> · {INBOUND_CORRECTION_LABELS[c.kind]}
                  <div className="muted" style={{ fontSize: 12 }}>
                    {correctionSummary(c)}
                    {c.moves.length > 0 && ` · ${movesText(c.moves)}`}
                    {c.note && ` · “${c.note}”`}
                  </div>
                </td>
                <td className="muted" data-label="Kim">
                  {c.correctedBy ?? '—'}
                  {c.shipmentClosed && ' · kapattıktan sonra'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

const cm = (v: string) => Math.round(Number(v.replace(',', '.')) * 10);
const kg = (v: string) => Math.round(Number(v.replace(',', '.')) * 1000);

/**
 * The size of a product nobody measured yet, taken from the first unit at the dock: a tape measure
 * and a scale, once per product. The seller does not have to type it.
 */
function MeasureCard(props: { line: InboundShipmentLine; onSaved: (desi: number | null) => void; onLater: () => void }) {
  const [values, setValues] = useState({ length: '', width: '', height: '', weight: '' });
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const size: ProductSize = { lengthMm: cm(values.length), widthMm: cm(values.width), heightMm: cm(values.height), grossWeightG: kg(values.weight) };
  const ready = Object.values(size).every((n) => Number.isFinite(n) && n >= 1);
  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      const r = await api<{ desi: number | null }>('PUT', `/api/wms/products/${props.line.productId}/size`, size);
      props.onSaved(r.desi);
    } catch (e) {
      setError(errorMessage(e));
      setBusy(false);
    }
  };
  const field = (key: keyof typeof values, label: string, unit: string) => (
    <div className="field" style={{ flex: 1, minWidth: 90 }}>
      <label htmlFor={`m-${key}`}>
        {label} ({unit})
      </label>
      <input
        id={`m-${key}`}
        className="input"
        inputMode="decimal"
        value={values[key]}
        onChange={(e) => setValues({ ...values, [key]: e.target.value })}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && ready && !busy) void save();
        }}
      />
    </div>
  );
  return (
    <div className="banner info-soft" style={{ display: 'block' }}>
      <div className="row" style={{ gap: 8 }}>
        <Icon name="ruler" />
        <b>{props.line.productName}</b>
        <span className="muted">ölçüsü yok: bir tanesini ölçün (bir kez)</span>
      </div>
      {error && (
        <div className="banner bad" style={{ marginTop: 8 }}>
          {error}
        </div>
      )}
      <div className="row" style={{ alignItems: 'flex-end', marginTop: 8 }}>
        {field('length', 'En', 'cm')}
        {field('width', 'Boy', 'cm')}
        {field('height', 'Yükseklik', 'cm')}
        {field('weight', 'Ağırlık', 'kg')}
      </div>
      <div className="row" style={{ marginTop: 8 }}>
        <button className="btn primary" disabled={!ready || busy} onClick={() => void save()}>
          Ölçüyü kaydet
        </button>
        <button className="btn" onClick={props.onLater}>
          Sonra
        </button>
        <span className="muted" style={{ fontSize: 13 }}>
          Koli önerisi, raf kapasitesi ve depolama ücreti bu ölçüyle hesaplanır.
        </span>
      </div>
    </div>
  );
}

/** the product label roll of this computer, as "Etiket bas" and "Barkodu bas" keep it */
const LABEL_SIZE_KEY = LABEL_SIZE_KEYS.here;

/**
 * Goods that arrive without a barcode on them (the seller's code exists only on the marketplace):
 * the dock counts them, receives the count in one go and prints that many labels to stick on, so
 * picking and packing scan them like any other product. The label carries the product's barcode,
 * or its SKU when it has none. The count runs on the dock's runner: a lost one keeps its key after
 * the dialog closes and is sent again from the dock (counted again with a new key, it would be stock
 * that is not there).
 */
function NoBarcodeDialog(props: {
  shipmentId: string;
  sellerCode: string;
  line: InboundShipmentLine;
  runner: FloorActionRunner;
  onClose: () => void;
  onReceived: (result: ReceiveScanResult, labels: { printed: number; error: string | null }) => void;
}) {
  const l = props.line;
  const left = Math.max(0, l.expectedQty - l.receivedQty - l.damagedQty);
  const code = l.barcode ?? l.sku;
  const printable = /^[!-~]{3,64}$/.test(code);
  // empty until the worker types what they counted
  const [qty, setQty] = useState('');
  // the seller wants no labels on the goods: one card for the shelf or pallet instead
  const [cards, setCards] = useState('1');
  const [lot, setLot] = useState(l.lotNo ?? '');
  const [expiry, setExpiry] = useState(l.expiryDate ?? '');
  const [damaged, setDamaged] = useState(false);
  const [label, setLabel] = useState(printable);
  const [size, setSize] = useState(() => {
    const saved = store.get(LABEL_SIZE_KEY);
    return saved && parseLabelSize(saved) ? saved : '50x30';
  });
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const count = qty === '' ? 0 : Number(qty);
  const valid = count >= 1 && count <= 10_000 && (!l.trackLot || lot.trim()) && (!l.trackExpiry || expiry);
  const labels = label && printable && !damaged;
  const copies = l.unitLabels ? count : Math.max(1, Math.min(20, Math.floor(Number(cards)) || 1));
  const { runner } = props;

  const receive = async (action: FloorAction) => {
    setBusy(true);
    setError(null);
    let result: ReceiveScanResult;
    try {
      result = await api<ReceiveScanResult>(
        'POST',
        `/api/wms/receiving/${props.shipmentId}/scan`,
        {
          barcode: code,
          qty: count,
          ...(lot.trim() ? { lotNo: lot.trim() } : {}),
          ...(expiry ? { expiryDate: expiry } : {}),
          ...(damaged ? { condition: 'damaged' } : {}),
        },
        { action },
      );
    } catch (e) {
      feedback('error');
      setError(errorMessage(e));
      setBusy(false);
      return;
    }
    feedback('ok');
    // the receipt stands even if the printer fails: the labels can be printed again from the product
    let printed = 0;
    let printError: string | null = null;
    if (labels) {
      try {
        store.set(LABEL_SIZE_KEY, size);
        const zpl = productLabelZpl({ sku: l.sku, name: l.productName, barcode: code, size, lotNo: lot.trim() || null, expiryDate: expiry || null, sellerCode: props.sellerCode, copies });
        printed = (await printZpl(zpl, `${l.sku} etiketleri`)).pages;
      } catch (e) {
        printError = errorMessage(e);
      }
    }
    props.onReceived(result, { printed, error: printError });
  };

  const send = () =>
    void runner.run('Barkodsuz kabul', receive).then((sent) => {
      if (!sent) setError('Önceki işlem henüz bitmedi. Bekleyip tekrar deneyin.');
    });
  // not while the count is on its way: its answer decides what the dock shows
  const close = () => {
    if (!busy) props.onClose();
  };
  return (
    <Modal
      title={`${l.productName} · barkodsuz kabul`}
      onClose={close}
      footer={
        <>
          <button className="btn" disabled={busy} onClick={close}>
            Vazgeç
          </button>
          <button className="btn primary" disabled={!valid || busy || runner.waiting} onClick={send}>
            <Icon name={labels ? 'printer' : 'check'} />{' '}
            {count > 0 ? `${fmt.n(count)} adet kabul et${labels ? (l.unitLabels ? ' ve etiket bas' : ' ve ürün kartı bas') : ''}` : 'Önce adedi yazın'}
          </button>
        </>
      }
    >
      <div className="stack rcv-dialog">
        {error && <div className="banner bad">{error}</div>}
        {runner.banner}
        {l.unitLabels ? (
          <p className="muted" style={{ margin: 0 }}>
            Üzerinde barkod olmayan ürünler için: sayın, adedi girin; kabul kaydedilir ve her ürüne yapıştırılacak etiket basılır. Toplama ve
            paketlemede bu etiket okutulur.
          </p>
        ) : (
          <div className="banner info-soft">
            Satıcı bu ürüne etiket yapıştırılmasını istemiyor. Sayıp kabul edin; rafa ya da palete konacak ürün kartı basılır. Toplayıcı kartı
            okutur, paketlemede ürün gözle onaylanır.
          </div>
        )}
        <div className="row" style={{ alignItems: 'flex-start' }}>
          <CountBox id="nb-qty" value={qty} left={left} onChange={setQty} onEnter={() => valid && !busy && !runner.waiting && send()} />
          {l.trackLot && (
            <div className="field" style={{ flex: 1, minWidth: 140 }}>
              <label htmlFor="nb-lot">Lot</label>
              <input id="nb-lot" className="input mono" value={lot} onChange={(e) => setLot(e.target.value.toUpperCase())} placeholder="Zorunlu" />
            </div>
          )}
          {l.trackExpiry && (
            <div className="field" style={{ flex: 1, minWidth: 160 }}>
              <label htmlFor="nb-expiry">Son kullanma tarihi</label>
              <input id="nb-expiry" className="input" type="date" value={expiry} onChange={(e) => setExpiry(e.target.value)} />
            </div>
          )}
        </div>
        <label className="row rcv-check" style={{ gap: 8 }}>
          <input type="checkbox" checked={damaged} onChange={(e) => setDamaged(e.target.checked)} />
          Hasarlı (hasarlı alanına ayrılır, etiket basılmaz)
        </label>
        {printable ? (
          <>
            <label className="row rcv-check" style={{ gap: 8 }}>
              <input type="checkbox" checked={label} disabled={damaged} onChange={(e) => setLabel(e.target.checked)} />
              {l.unitLabels ? 'Her ürün için etiket bas' : 'Ürün kartı bas'} · barkod <span className="mono">{code}</span>
              {!l.barcode && <span className="muted">(ürünün barkodu yok, stok kodu basılır)</span>}
            </label>
            {labels && !l.unitLabels && (
              <div className="field" style={{ maxWidth: 160 }}>
                <label htmlFor="nb-cards">Kart adedi (raf / palet)</label>
                <input id="nb-cards" className="input" inputMode="numeric" value={cards} onChange={(e) => setCards(e.target.value.replace(/\D/g, ''))} />
              </div>
            )}
            {labels && (
              <div className="field">
                <label htmlFor="nb-size">Etiket boyutu (yazıcıdaki rulo)</label>
                <LabelSizePicker id="nb-size" value={size} onChange={setSize} />
              </div>
            )}
          </>
        ) : (
          <div className="banner warn">
            Bu ürünün basılabilir bir barkodu yok. Ürünler ekranında ürünü açıp “İç barkod üret” deyin, sonra buradan etiket basın.
          </div>
        )}
      </div>
    </Modal>
  );
}

/**
 * A supervisor takes goods in by count, with no barcode read (no reader at hand, or a label that
 * will not read). The API refuses it for anyone else.
 */
function HandReceiveDialog(props: { line: InboundShipmentLine; lotNo: string; expiry: string; onClose: () => void; onReceive: (take: Take) => void }) {
  const { line } = props;
  const counted = line.receivedQty + line.damagedQty;
  // empty until the supervisor types what they counted
  const [qty, setQty] = useState('');
  const [lotNo, setLotNo] = useState(props.lotNo);
  const [expiry, setExpiry] = useState(props.expiry);
  const [damaged, setDamaged] = useState(false);
  const n = qty === '' ? 0 : Number(qty);
  const valid = n >= 1 && n <= 10_000 && (!line.trackLot || lotNo.trim() !== '') && (!line.trackExpiry || expiry !== '');
  const receive = () => valid && props.onReceive({ barcode: line.sku, productId: line.productId, qty: n, lotNo: lotNo.trim(), expiry, damaged });
  return (
    <Modal
      title={`${line.sku} · okutmadan kabul`}
      onClose={props.onClose}
      footer={
        <>
          <button className="btn" onClick={props.onClose}>
            Vazgeç
          </button>
          <button className="btn primary" disabled={!valid} onClick={receive}>
            {n > 0 ? `${fmt.n(n)} adet kabul et` : 'Önce adedi yazın'}
          </button>
        </>
      }
    >
      <div className="stack rcv-dialog">
        <p style={{ margin: 0 }}>
          <b>{line.productName}</b>: beklenen {fmt.n(line.expectedQty)}, sayılan {fmt.n(counted)}. Barkod okutmadan saydığınız adedi yazın.
        </p>
        <div className="row" style={{ alignItems: 'flex-start' }}>
          <CountBox id="hand-qty" value={qty} left={Math.max(0, line.expectedQty - counted)} onChange={setQty} onEnter={receive} />
          {line.trackLot && (
            <div className="field" style={{ flex: 1, minWidth: 140 }}>
              <label htmlFor="hand-lot">Lot</label>
              <input id="hand-lot" className="input mono" value={lotNo} onChange={(e) => setLotNo(e.target.value.toUpperCase())} />
            </div>
          )}
          {line.trackExpiry && (
            <div className="field" style={{ flex: 1, minWidth: 160 }}>
              <label htmlFor="hand-expiry">Son kullanma tarihi</label>
              <input id="hand-expiry" className="input" type="date" value={expiry} onChange={(e) => setExpiry(e.target.value)} />
            </div>
          )}
        </div>
        <label className="row rcv-check">
          <input type="checkbox" checked={damaged} onChange={(e) => setDamaged(e.target.checked)} />
          Hasarlı (hasarlı alanına ayrılır, satışa açılmaz)
        </label>
      </div>
    </Modal>
  );
}

/**
 * The count in a dialog: empty until the worker types what they counted (the expected rest is a
 * hint under it, never a pre-filled guess); focusing it selects what is there, so typing replaces it.
 */
function CountBox(props: { id: string; value: string; left: number; onChange: (value: string) => void; onEnter?: () => void }) {
  return (
    <div className="field rcv-count-box">
      <label htmlFor={props.id}>Sayılan adet</label>
      <input
        id={props.id}
        className="input"
        inputMode="numeric"
        autoComplete="off"
        autoFocus
        placeholder="Adet"
        value={props.value}
        onFocus={(e) => e.target.select()}
        onMouseUp={(e) => e.preventDefault()}
        onChange={(e) => props.onChange(e.target.value.replace(/\D/g, '').slice(0, 5))}
        onKeyDown={(e) => {
          if (e.key === 'Enter') props.onEnter?.();
        }}
      />
      <span className="hint">Beklenen kalan: {fmt.n(props.left)}</span>
    </div>
  );
}

const PER_SCAN_CHOICES = [1, 5, 10, 12, 24];

/** Back to the screen's scan field (there is one per screen), where the scanner types. */
function focusScanField() {
  document.querySelector<HTMLInputElement>('.scan input')?.focus();
}

/**
 * How many one scan counts. Typed freely; Enter (or leaving the box) keeps it, and Enter sends the
 * cursor back to the scan field. A handheld scanner types a barcode as one fast run that ends with
 * Enter: when the count was typed and the product scanned at once, that run lands here, and it is
 * taken as a scan with that count, never as a count of thousands. A staff member's code here must be
 * a reader's as in the scan field: pasted, corrected or held down, it is refused.
 */
function PerScanCount(props: {
  qty: number;
  keep: boolean;
  onQty: (qty: number) => void;
  onKeep: (keep: boolean) => void;
  onScan: (barcode: string, qty: number) => void;
}) {
  const [text, setText] = useState(String(props.qty));
  // the screen sets the count back to 1 after a scan
  const [shown, setShown] = useState(props.qty);
  if (shown !== props.qty) {
    setShown(props.qty);
    setText(String(props.qty));
  }
  // the box after each change, and when it came
  const typed = useRef<{ value: string; at: number }[]>([]);
  // how the text came (its changes and keys): the staff read codes with a reader, supervisors may type them
  const scannerOnly = useScannerOnly();
  const trace = useRef(createScanRecorder());
  const [refused, setRefused] = useState<ScanVerdict | null>(null);

  const settle = (value: string, back: boolean) => {
    const log = typed.current;
    const how = trace.current.trace();
    typed.current = [];
    trace.current.clear();
    if (log.length > 0) {
      // the reader's run at the end (one change when a scanner sends the text at once, bursts from a
      // Bluetooth one): four or more characters in it are no hand's typing but a barcode
      const cut = readerTail(log, value);
      if (cut === null) {
        const n = typedCount(value);
        setText(String(n ?? props.qty));
        if (n !== null) props.onQty(n);
      } else {
        const n = typedCount(value.slice(0, cut)) ?? props.qty;
        const barcode = normalizeScan(value.slice(cut));
        setText(String(n));
        props.onQty(n);
        // the code alone, as the scan field judges one: pasted, dropped, corrected or a key held down is no reader's
        const verdict = judgeScan(traceAfter(how, cut), value.slice(cut));
        if (scannerOnly && !verdict.reader) {
          setRefused(verdict);
          logRefusal(verdict);
          feedback('error');
        } else if (barcode) props.onScan(barcode, n);
      }
    }
    if (back) focusScanField();
  };

  return (
    <div className="field">
      <label htmlFor="rcv-qty">Bir okutmada sayılacak adet</label>
      <div className="row">
        <input
          id="rcv-qty"
          className="input"
          inputMode="numeric"
          autoComplete="off"
          style={{ width: 90 }}
          value={text}
          onChange={(e) => {
            typed.current.push({ value: e.target.value, at: e.timeStamp });
            trace.current.change(e);
            setRefused(null);
            setText(e.target.value);
          }}
          // the whole count is selected, so typing replaces it (a click would put the cursor in it)
          onFocus={(e) => e.target.select()}
          onMouseUp={(e) => e.preventDefault()}
          onKeyDown={(e) => {
            trace.current.key(e);
            if (e.key !== 'Enter') return;
            e.preventDefault();
            settle(e.currentTarget.value, true);
          }}
          onBlur={(e) => settle(e.currentTarget.value, false)}
        />
        {PER_SCAN_CHOICES.map((n) => (
          <button
            key={n}
            className={`chip select${props.qty === n ? ' on' : ''}`}
            onClick={() => {
              props.onQty(n);
              focusScanField();
            }}
          >
            ×{n}
          </button>
        ))}
        <label className="row rcv-check" style={{ gap: 6 }}>
          <input type="checkbox" checked={props.keep} onChange={(e) => props.onKeep(e.target.checked)} />
          Sabit kalsın
        </label>
      </div>
      {refused ? (
        <div className="scan-hint bad" role="alert">
          {TYPED_CODE_REFUSED}
          <ScanWhy verdict={refused} />
        </div>
      ) : (
        <span className="hint">
          Barkodu olmayan kolide adedi yazıp ürünü bir kez okutun.{props.keep ? ' Okutmadan sonra da aynı kalır.' : ' Okutmadan sonra 1’e döner.'}
        </span>
      )}
    </div>
  );
}

/** A barcode scanned at the dock that no product of the seller had. */
interface ScannedUnknown {
  readonly barcode: string;
  /** a carton GTIN of a known unit: its carton size is what is missing */
  readonly carton: boolean;
  readonly productId: string | null;
  /** how many the scan counts once saved (units, or cartons) */
  readonly qty: number;
  /** another seller's product carries it: goods mixed into the carton? */
  readonly otherSeller: boolean;
}

/** "TEA-1: 8690… (ürün), KOLI-12 (koli, 12 adet) kaydedildi" */
function taughtText(t: DockBarcodesTaught): string {
  const said = (b: DockBarcode) => `${b.barcode} (${b.unitsPerScan > 1 ? `koli, ${b.unitsPerScan} adet` : 'ürün'})`;
  const parts = [
    ...(t.added.length > 0 ? [`${t.added.map(said).join(', ')} kaydedildi`] : []),
    ...(t.known.length > 0 ? [`${t.known.map(said).join(', ')} zaten kayıtlıydı`] : []),
  ];
  return `${t.sku}: ${parts.join('; ')}`;
}

/**
 * Barcodes on the goods that the seller never gave, saved to a product of the shipment: the unit's
 * own and the carton's (with how many units it holds), either or both. Opened by an unknown scan,
 * which is one of the two and counts once saved, or from a line ("Barkod ekle"). Scanning them
 * then counts; a carton barcode counts the whole carton, which can stay closed on the shelf.
 * After an unknown scan nothing is chosen for the worker: they tap the product it is on, or set
 * the item aside when it is no product of the shipment (nothing saved, nothing counted).
 */
function BarcodeDialog(props: {
  shipmentId: string;
  lines: readonly InboundShipmentLine[];
  /** opened from this line: its product */
  line?: InboundShipmentLine | undefined;
  scanned?: ScannedUnknown | undefined;
  onClose: () => void;
  /** "Tanımıyorum / gönderide yok": nothing saved, nothing counted */
  onSetAside: (barcode: string) => void;
  /** perScan: a carton with no barcode of its own, N units each: one scan of the product counts N from now on */
  onTaught: (taught: DockBarcodesTaught | null, perScan: number | null) => void;
}) {
  // one row per product, with what is still expected of it (a product may have a line per lot)
  const products = [...new Map(props.lines.map((l) => [l.productId, l])).values()].map((l) => ({
    line: l,
    left: props.lines.filter((x) => x.productId === l.productId).reduce((n, x) => n + Math.max(0, x.expectedQty - x.receivedQty - x.damagedQty), 0),
  }));
  // the ones still expected first
  const listed = [...products.filter((p) => p.left > 0), ...products.filter((p) => p.left === 0)];
  // known for sure: the line it was opened from, or the unit behind a carton GTIN; an unknown code is never guessed
  const fixed = props.line ?? (props.scanned?.carton ? products.find((p) => p.line.productId === props.scanned?.productId)?.line : undefined);
  const [productId, setProductId] = useState(fixed?.productId ?? '');
  // the scanned barcode is the unit's or the carton's
  const [scannedCarton, setScannedCarton] = useState(props.scanned?.carton ?? false);
  const [unitCode, setUnitCode] = useState('');
  const [cartonCode, setCartonCode] = useState('');
  // how each code got into its box: the staff read them with a reader, supervisors may type them
  const scannerOnly = useScannerOnly();
  const unitTrace = useRef(createScanRecorder());
  const cartonTrace = useRef(createScanRecorder());
  // a refused code's timing, under the refusal
  const [refusal, setRefusal] = useState<ScanVerdict | null>(null);
  // a line announced in cartons tells how many one holds
  const caseOf = (id: string) => {
    const line = products.find((p) => p.line.productId === id)?.line;
    return line?.packagingLevel === 'case' ? String(line.unitsPerPack) : '';
  };
  const [perCarton, setPerCarton] = useState(() => caseOf(fixed?.productId ?? ''));
  // only a count the worker typed makes every scan count a carton (a pre-filled one is for a carton barcode)
  const [countTyped, setCountTyped] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const scanned = props.scanned?.barcode ?? null;
  // an unknown code: the worker says which product it is on, or sets it aside
  const choosing = scanned !== null && !fixed;
  const chosen = products.find((p) => p.line.productId === productId)?.line ?? null;
  const unit = scanned !== null && !scannedCarton ? scanned : unitCode.trim();
  const carton = scanned !== null && scannedCarton ? scanned : cartonCode.trim();
  const inCarton = Number(perCarton);
  const countOk = Number.isInteger(inCarton) && inCarton >= 2 && inCarton <= 10_000;
  const cartonOk = !carton || countOk;
  const knownCode = chosen?.barcode ?? null;
  // a carton count with no carton barcode: the product's own barcode is scanned once per carton
  const perScanOnly = !carton && countTyped && perCarton !== '' && countOk && Boolean(unit || knownCode);
  const valid = chosen !== null && (Boolean(unit || carton) || perScanOnly) && cartonOk && (perCarton === '' || countOk);
  // what the scan counts once saved
  const counted = props.scanned && valid ? (perScanOnly ? inCarton : props.scanned.qty * (scannedCarton ? inCarton : 1)) : null;
  // the first box to fill: the one still missing (after an unknown unit code everything else is optional, so none)
  const focus = props.scanned ? (props.scanned.carton ? 'bd-per-carton' : null) : knownCode ? 'bd-carton' : 'bd-unit';

  const choose = (id: string) => {
    // another product: its own carton size, nothing carried over
    setProductId(id);
    setPerCarton(caseOf(id));
    setCountTyped(false);
    setError(null);
  };

  const save = async () => {
    if (!valid || busy) return;
    // a code typed into a box (the scanned one came through the scan field, already judged)
    const judge = (code: string, trace: ScanRecorder) => (code !== '' && code !== scanned ? judgeScan(trace.trace(), code) : null);
    const unitRead = judge(unit, unitTrace.current);
    const cartonRead = judge(carton, cartonTrace.current);
    const typedOne = [unitRead, cartonRead].find((v) => v !== null && !v.reader);
    if (scannerOnly && typedOne) {
      setError(TYPED_CODE_REFUSED);
      setRefusal(typedOne);
      logRefusal(typedOne);
      return;
    }
    setBusy(true);
    setError(null);
    setRefusal(null);
    // saved as read: a code may hold a '+', '*' or '=' of its own (a reader on another keyboard layout is
    // set right by the look-ups, after the code itself)
    try {
      const barcodes = [...(unit ? [{ barcode: unit, unitsPerScan: 1 }] : []), ...(carton ? [{ barcode: carton, unitsPerScan: inCarton }] : [])];
      const taught = barcodes.length > 0 ? await api<DockBarcodesTaught>('POST', `/api/wms/receiving/${props.shipmentId}/barcodes`, { productId, barcodes }) : null;
      props.onTaught(taught, perScanOnly ? inCarton : null);
    } catch (e) {
      setError(errorMessage(e));
      setBusy(false);
    }
  };
  // a scanner ends with Enter: on to the next box that takes a value, and after the last one, save
  const onEnter =
    (...next: string[]) =>
    (e: KeyboardEvent<HTMLInputElement>) => {
      if (e.key !== 'Enter') return;
      e.preventDefault();
      const box = next
        .map((id) => document.getElementById(id))
        .find((el): el is HTMLInputElement => el instanceof HTMLInputElement && !el.disabled && !el.readOnly);
      if (box) box.focus();
      else void save();
    };

  return (
    <Modal
      title={props.line ? `${props.line.sku} · barkod ekle` : 'Tanımsız barkod'}
      onClose={props.onClose}
      footer={
        <>
          <button className="btn" onClick={props.onClose}>
            Vazgeç
          </button>
          <button className="btn primary" disabled={!valid || busy} onClick={() => void save()}>
            {!props.scanned
              ? 'Barkodları kaydet'
              : chosen === null
                ? 'Önce ürünü seçin'
                : counted === null
                  ? 'Kaydet ve kabul et'
                  : `Kaydet ve ${scannedCarton && props.scanned.qty > 1 ? `${props.scanned.qty} koli = ` : ''}${fmt.n(counted)} adet kabul et`}
          </button>
        </>
      }
    >
      <div className="stack rcv-dialog">
        {error && (
          <div className="banner bad">
            <div style={{ flex: 1, minWidth: 0 }}>
              {error}
              {refusal && error === TYPED_CODE_REFUSED && <ScanWhy verdict={refusal} />}
            </div>
          </div>
        )}
        {props.scanned?.otherSeller && (
          <div className="banner warn">
            <Icon name="alert" />
            <div style={{ flex: 1, minWidth: 0 }}>
              <b>Bu barkod başka bir satıcının ürününde kayıtlı — koliye karışmış olabilir.</b> Ürün bu gönderinin değilse aşağıdan “ayrı koy”u seçin.
            </div>
          </div>
        )}
        <p style={{ margin: 0 }}>
          {scanned !== null ? (
            props.scanned?.carton ? (
              <>
                <span className="mono">{scanned}</span> bir koli barkodu ama kolide kaç ürün olduğu kayıtlı değil. Koliyi sayıp adedi yazın.
              </>
            ) : (
              <>
                <span className="mono">{scanned}</span> bu satıcının ürünlerinde kayıtlı değil. Barkod hangi ürünün üstünde? Dokunup seçin; kaydedince
                okutma sayılır ve bundan sonra tanınır.
              </>
            )
          ) : (
            'Ürünün ve kolisinin üstündeki barkodları okutun; biri yeter. Bundan sonra okutunca tanınır, koli barkodu kolideki bütün ürünleri sayar. Kaydedince etiketini de basabilirsiniz (“Barkodu bas”).'
          )}
        </p>
        {choosing ? (
          <>
            <div className="rcv-picks" role="radiogroup" aria-label="Barkodun ürünü">
              {listed.map(({ line: l, left }) => (
                <button
                  key={l.productId}
                  type="button"
                  role="radio"
                  aria-checked={l.productId === productId}
                  className={`rcv-pick${l.productId === productId ? ' on' : ''}`}
                  onClick={() => choose(l.productId)}
                >
                  <span className="rcv-pick-name">
                    <b>{l.productName}</b>
                    <small className="mono">{l.sku}</small>
                  </span>
                  <span className="rcv-pick-left">{left > 0 ? <><b>{fmt.n(left)}</b> kalan</> : 'hepsi geldi'}</span>
                </button>
              ))}
            </div>
            <div className="rcv-aside">
              <span className="muted">Barkod bu ürünlerin hiçbirinde değilse:</span>
              <button type="button" className="btn block" onClick={() => props.onSetAside(scanned)}>
                <Icon name="alert" /> Tanımıyorum / gönderide yok — ayrı koy
              </button>
            </div>
          </>
        ) : (
          chosen &&
          !props.line && (
            <div className="rcv-pick on" aria-label="Ürün">
              <span className="rcv-pick-name">
                <b>{chosen.productName}</b>
                <small className="mono">{chosen.sku}</small>
              </span>
            </div>
          )
        )}
        {chosen && (
          <>
            {scanned !== null && !props.scanned?.carton && (
              <div className="choice-list">
                <label className={`choice${!scannedCarton ? ' on' : ''}`}>
                  <input type="radio" name="bd-kind" checked={!scannedCarton} onChange={() => setScannedCarton(false)} />
                  <span>
                    <b>Okuttuğum, ürünün kendi barkodu</b>
                    <small>Bir okutma 1 adet sayar</small>
                  </span>
                </label>
                <label className={`choice${scannedCarton ? ' on' : ''}`}>
                  <input type="radio" name="bd-kind" checked={scannedCarton} onChange={() => setScannedCarton(true)} />
                  <span>
                    <b>Okuttuğum, kolinin barkodu</b>
                    <small>Bir okutma kolideki bütün ürünleri sayar; koli rafta açılmadan durabilir</small>
                  </span>
                </label>
              </div>
            )}
            <div className="field">
              <label htmlFor="bd-unit">Ürünün kendi barkodu{scanned !== null && !scannedCarton ? '' : ' (varsa)'}</label>
              <input
                id="bd-unit"
                className="input mono"
                autoComplete="off"
                autoFocus={focus === 'bd-unit'}
                placeholder="Ürünü okutun"
                readOnly={scanned !== null && !scannedCarton}
                value={scanned !== null && !scannedCarton ? scanned : unitCode}
                onChange={(e) => {
                  unitTrace.current.change(e);
                  setUnitCode(e.target.value);
                }}
                onKeyDown={(e) => {
                  unitTrace.current.key(e);
                  onEnter('bd-carton', 'bd-per-carton')(e);
                }}
              />
              <span className="hint">
                {knownCode ? `Kayıtlı ürün barkodu: ${knownCode}. Ürün başka bir barkod da taşıyorsa okutun.` : 'Ürünün üstünde barkod yoksa boş bırakın.'}
              </span>
            </div>
            <div className="row" style={{ alignItems: 'flex-start' }}>
              <div className="field" style={{ flex: 1, minWidth: 180 }}>
                <label htmlFor="bd-carton">Koli barkodu{scanned !== null && scannedCarton ? '' : ' (varsa)'}</label>
                <input
                  id="bd-carton"
                  className="input mono"
                  autoComplete="off"
                  autoFocus={focus === 'bd-carton'}
                  placeholder="Koliyi okutun"
                  readOnly={scanned !== null && scannedCarton}
                  value={scanned !== null && scannedCarton ? scanned : cartonCode}
                  onChange={(e) => {
                    cartonTrace.current.change(e);
                    setCartonCode(e.target.value);
                  }}
                  onKeyDown={(e) => {
                    cartonTrace.current.key(e);
                    onEnter('bd-per-carton')(e);
                  }}
                />
              </div>
              <div className="field" style={{ width: 150 }}>
                <label htmlFor="bd-per-carton">Kolide kaç adet</label>
                <input
                  id="bd-per-carton"
                  className={`input${perCarton && !countOk ? ' invalid' : ''}`}
                  inputMode="numeric"
                  autoComplete="off"
                  autoFocus={focus === 'bd-per-carton'}
                  placeholder="ör. 12"
                  value={perCarton}
                  onChange={(e) => {
                    setPerCarton(e.target.value.replace(/\D/g, '').slice(0, 5));
                    setCountTyped(true);
                  }}
                  onKeyDown={onEnter()}
                />
              </div>
            </div>
            {!carton && countTyped && perCarton !== '' && (
              <div className={`banner ${perScanOnly ? 'info-soft' : 'warn'}`} style={{ padding: '10px 12px' }}>
                {!countOk
                  ? 'Koli adedi 2 ile 10.000 arasında olmalı.'
                  : perScanOnly
                    ? `Kolinin kendi barkodu yok: kaydedince kabulde her okutma ${inCarton} adet sayar (“Bir okutmada sayılacak adet” ${inCarton}, sabit). Her koliden bir ürünü okutun; başka ürüne ya da tek tek saymaya geçerken ×1’e dönün.`
                    : 'Kolinin barkodu yoksa ürünün kendi barkodunu okutun; her okutması koli adedi kadar sayılır.'}
              </div>
            )}
          </>
        )}
      </div>
    </Modal>
  );
}
